import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Upload,
  message,
  App,
  Select,
} from "antd";
import {
  LeftOutlined,
  Loading3QuartersOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { VIEW_ANNOUNCEMENT, EDIT_ANNOUNCEMENT } from "../../apis/apis";
import CulsightPageLoader from "../../components/CulsightPageLoader";
import CustomTextEditor from "../../components/CustomTextEditor";


export default function EditAnnouncement() {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams(); // expecting encoded id as earlier code used atob(id)

  const [form] = Form.useForm();
  const [page_loader, set_page_loader] = useState(true); // fetching data loader
  const   [loading, setLoading] = useState(false); // submit loader
  const [errors, set_errors] = useState({});

  // image preview & new file
  const [imagePreview, setImagePreview] = useState("");
  const [newImageFile, setNewImageFile] = useState(null);
  const [imageError, setImageError] = useState("");

  // helper to convert file -> base64 preview
  const getBase64 = (file, cb) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => cb(reader.result));
    reader.readAsDataURL(file);
  };

useEffect(() => {
  const fetchData = async () => {
    set_page_loader(true);
    try {
      if (!id) {
        message.error("Announcement ID missing");
        set_page_loader(false);
        return;
      }

      const numericId = Number(id); // 👈 directly number, no atob
      if (isNaN(numericId)) {
        message.error("Invalid Announcement ID");
        set_page_loader(false);
        return;
      }
      

      const FD = new FormData();
      FD.append("id", numericId);

      const res = await VIEW_ANNOUNCEMENT(FD);

      if (res?.data?.status) {
        const d = res.data.data || {};
        form.setFieldsValue({
          title: d.title || "",
          short_description: d.short_description || "",
          description: d.description || "",
          tags: d.tags|| "",
          slug: d.slug || "",
          seo_title: d.seo_title || "",
          seo_description: d.seo_description || "",
          seo_keywords: d.seo_keywords || "",
          location: d.location || "",
          video_url: d.video_url || "",
          source: d.source || "",
          priority: d.priority || "",
        });

        if (d.image_url) {
          setImagePreview(d.image_url);
          setNewImageFile(null);
        }
      } else {
        message.error("Failed to load announcement details");
      }
    } catch (err) {
      message.error("Server Error while fetching details");
    } finally {
      set_page_loader(false);
    }
  };

  fetchData();
}, [id]);


  
  const handleBeforeUpload = (file) => {
    setImageError("");
    set_errors((p) => ({ ...p, image: "" }));

    const allowed = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowed.includes(file.type)) {
      setImagePreview("");
      setNewImageFile(null);
      setImageError("Only JPG/PNG files are allowed.");
      return false;
    }

    if (file.size > 2 * 1024 * 1024) {
      setImagePreview("");
      setNewImageFile(null);
      setImageError("Image must be ≤ 2MB.");
      return false;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const { width, height } = img;
      if (width === 600 && height === 400) {
        setImageError("");
       
        getBase64(file, (url) => setImagePreview(url));
        setNewImageFile(file);
      } else {
        setImagePreview("");
        setNewImageFile(null);
        setImageError("Image must be exactly 600x400 px.");
      }
    };

    return false; 
  };

  const clearFieldError = (field) => {
    set_errors((prev) => ({ ...prev, [field]: "" }));
  };

 const onFinish = async () => {
  setLoading(true);
  set_errors({});

  try {
    const numericId = Number(id); 
    if (isNaN(numericId)) {
      message.error("Invalid announcement ID");
      setLoading(false);
      return;
    }

    const FD = new FormData();
    FD.append("id", numericId);

    const values = form.getFieldsValue();
    Object.keys(values).forEach((k) => {
      FD.append(k, values[k] == null ? "" : values[k]);
    });

    if (newImageFile) {
      FD.append("image", newImageFile);
    }

    const res = await EDIT_ANNOUNCEMENT(FD);

    if (res?.data?.status) {
      notification.success({
        message: "Successful",
        description: res.data.message || "Announcement updated successfully",
      });
      navigate("/announcements");
    } else {
      set_errors(res?.data?.errors || {});
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  } catch (err) {
    message.error("Server Error, Try Later!");
  } finally {
    setLoading(false);
  }
};


  if (page_loader) {
    return (
      <div className="lms-body">
        <Card>
          <CulsightPageLoader />
        </Card>
      </div>
    );
  }

  return (
    <div className="lms-body">
      <Card>
        <div className="lms-form">
          <Row>
            <Col span={12}>
              <h2>
                <span
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/announcements")}
                >
                  <LeftOutlined />
                </span>{" "}
                Edit Announcement
              </h2>
            </Col>
          </Row>

          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            onFinish={onFinish}
          >
           
            <Form.Item
              label="Upload Thumbnail (Optional)"
              validateStatus={imageError || errors?.image ? "error" : ""}
              help={imageError || errors?.image}
            >
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={handleBeforeUpload}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="thumbnail"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <button
                    type="button"
                    style={{ border: 0, background: "none" }}
                  >
                    <PlusOutlined style={{ color: "#fff" }} />
                    <div style={{ marginTop: 8, color: "#fff" }}>Upload</div>
                  </button>
                )}
              </Upload>

              <p style={{ color: "#65e7c4", marginTop: 8 }}>
                Note - Image must be JPG/PNG, ≤2MB and exactly 600x400 px. Leave empty to keep existing thumbnail.
              </p>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  validateStatus={errors?.title ? "error" : ""}
                  help={errors?.title}
                >
                  <Input onChange={() => clearFieldError("title")} placeholder="Enter title" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Short Description"
                  name="short_description"
                  validateStatus={errors?.short_description ? "error" : ""}
                  help={errors?.short_description}
                >
                  <Input onChange={() => clearFieldError("short_description")} placeholder="Short description" />
                </Form.Item>
              </Col>

              <Col span={12}>
                  <Form.Item
  label="Tags"
  name="tags"
  validateStatus={errors?.tags ? "error" : ""}
  help={errors?.tags}
>
  <Select
    mode="tags"
    placeholder="Add tags"
    style={{ width: "100%" }}
    onChange={(value) => {
      clearFieldError("tags");   // error clear karega
      form.setFieldsValue({ tags: value }); // form me value set karega
    }}
  />
</Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Slug"
                  name="slug"
                  validateStatus={errors?.slug ? "error" : ""}
                  help={errors?.slug}
                >
                  <Input onChange={() => clearFieldError("slug")} placeholder="Enter slug" />
                </Form.Item>
              </Col>
            </Row>

           <Form.Item
  label="Description"
  name="description"
  validateStatus={errors?.description ? "error" : ""}
  help={errors?.description}
>
  <CustomTextEditor
    height={300}
    onChange={() => clearFieldError("description")}
  />
</Form.Item>


            <h3>SEO Information</h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="SEO Title"
                  name="seo_title"
                  validateStatus={errors?.seo_title ? "error" : ""}
                  help={errors?.seo_title}
                >
                  <Input onChange={() => clearFieldError("seo_title")} placeholder="Seo title" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="SEO Description"
                  name="seo_description"
                  validateStatus={errors?.seo_description ? "error" : ""}
                  help={errors?.seo_description}
                >
                  <Input onChange={() => clearFieldError("seo_description")} placeholder="Seo description" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                 label="SEO Keywords"
                 name="seo_keywords"
                 validateStatus={errors?.seo_keywords ? "error" : ""}
                 help={errors?.seo_keywords}
               >
                 <Select
                   mode="tags"
                   placeholder="Add SEO keywords"
                   style={{ width: "100%" }}
                   onChange={(value) => {
                     clearFieldError("seo_keywords");  
                     form.setFieldsValue({ seo_keywords: value }); 
                   }}
                 />
               </Form.Item>
              </Col>
            </Row>

            <h3>Other Information</h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Location"
                  name="location"
                  validateStatus={errors?.location ? "error" : ""}
                  help={errors?.location}
                >
                  <Input onChange={() => clearFieldError("location")} placeholder="Add location" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Video URL"
                  name="video_url"
                  validateStatus={errors?.video_url ? "error" : ""}
                  help={errors?.video_url}
                >
                  <Input onChange={() => clearFieldError("video_url")} placeholder="Youtube video link" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Source"
                  name="source"
                  validateStatus={errors?.source ? "error" : ""}
                  help={errors?.source}
                >
                  <Input onChange={() => clearFieldError("source")} placeholder="Source info" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Priority"
                  name="priority"
                  validateStatus={errors?.priority ? "error" : ""}
                  help={errors?.priority}
                >
                  <Input onChange={() => clearFieldError("priority")} placeholder="Priority" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ float: "right" }}>
                {loading ? (
                  <>Saving... <Spin indicator={<LoadingOutlined spin />} /></>
                ) : (
                  "Save"
                )}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Card>
    </div>
  );
}
